import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Mail, Heart } from 'lucide-react';
import { Container } from '@/components/layout/Container';

export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-neutral-900 dark:bg-dark-base text-neutral-100 border-t border-neutral-800 dark:border-dark-border">
      {/* Main footer content */}
      <Container customPadding="px-4" className="max-w-[1440px] py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Brand section */}
          <div>
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-white mb-3">BillBee 🐝</h3>
              <p className="text-neutral-300 text-base leading-relaxed mb-6">
                Empowering citizens with comprehensive legislative insights, election data, and
                civic engagement tools.
              </p>
            </div>

            {/* Email link */}
            <div className="flex items-center gap-4">
              <a
                href="mailto:hello@billbee.ai"
                className="w-10 h-10 bg-neutral-800 dark:bg-dark-surface hover:bg-primary-600 rounded-lg flex items-center justify-center transition-colors duration-200 focus-visible-ring"
                aria-label="Send us an email"
              >
                <Mail className="w-5 h-5" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Platform section */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Platform</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/bills"
                  className="text-neutral-300 hover:text-white transition-colors duration-200 focus-visible-ring focus-visible-ring-inset text-sm"
                >
                  Legislative Bills
                </Link>
              </li>
              <li>
                <Link
                  to="/legislators"
                  className="text-neutral-300 hover:text-white transition-colors duration-200 focus-visible-ring focus-visible-ring-inset text-sm"
                >
                  Legislators
                </Link>
              </li>
              <li>
                <Link
                  to="/committees"
                  className="text-neutral-300 hover:text-white transition-colors duration-200 focus-visible-ring focus-visible-ring-inset text-sm"
                >
                  Committees
                </Link>
              </li>
              <li>
                <Link
                  to="/testimony"
                  className="text-neutral-300 hover:text-white transition-colors duration-200 focus-visible-ring focus-visible-ring-inset text-sm"
                >
                  Testimony
                </Link>
              </li>
              <li>
                <Link
                  to="/calendar"
                  className="text-neutral-300 hover:text-white transition-colors duration-200 focus-visible-ring focus-visible-ring-inset text-sm"
                >
                  Calendar
                </Link>
              </li>
              <li>
                <Link
                  to="/tools/find-your-legislator"
                  className="text-neutral-300 hover:text-white transition-colors duration-200 focus-visible-ring focus-visible-ring-inset text-sm"
                >
                  Find Your Legislator
                </Link>
              </li>
            </ul>
          </div>

          {/* Company section */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/about"
                  className="text-neutral-300 hover:text-white transition-colors duration-200 focus-visible-ring focus-visible-ring-inset text-sm"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/mission"
                  className="text-neutral-300 hover:text-white transition-colors duration-200 focus-visible-ring focus-visible-ring-inset text-sm"
                >
                  Our Mission
                </Link>
              </li>
              <li>
                <Link
                  to="/data-sources"
                  className="text-neutral-300 hover:text-white transition-colors duration-200 focus-visible-ring focus-visible-ring-inset text-sm"
                >
                  Data Sources
                </Link>
              </li>
              <li>
                <a
                  href="https://beehivemedia.org/contact/"
                  className="text-neutral-300 hover:text-white transition-colors duration-200 focus-visible-ring focus-visible-ring-inset text-sm inline-flex items-center gap-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contact Us
                  <ExternalLink className="w-3 h-3" aria-hidden="true" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </Container>

      {/* Bottom bar */}
      <div className="border-t border-neutral-800 dark:border-dark-border bg-neutral-950 dark:bg-dark-elevated">
        <Container customPadding="px-4" className="max-w-[1440px] py-6">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <p className="text-neutral-400 text-sm">
                © {currentYear} Beehive Media, LLC. All rights reserved.
              </p>
              <p className="text-neutral-500 text-xs flex items-center gap-1">
                Made with <Heart className="w-3 h-3 text-error-400" aria-hidden="true" /> for civic
                engagement
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6">
              <Link
                to="/privacy"
                className="text-neutral-400 hover:text-white text-sm transition-colors duration-200 focus-visible-ring focus-visible-ring-inset"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-neutral-400 hover:text-white text-sm transition-colors duration-200 focus-visible-ring focus-visible-ring-inset"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </Container>
      </div>
    </footer>
  );
};
